/**
 * Error Handler Utility
 * Converts technical blockchain errors to user-friendly messages
 */

/**
 * Maps technical error messages to user-friendly messages
 * @param {string} errorMessage - The raw error message from the blockchain
 * @returns {Object} - Object containing user-friendly message and technical details
 */
export const parseErrorMessage = (errorMessage) => {
  const error = errorMessage.toLowerCase();
  
  // Common blockchain error patterns (order matters: more specific first)
  const errorMappings = [
    {
      patterns: ['approved already'],
      userMessage: 'This reservation is already approved.',
      technicalMessage: 'Reservation already approved on-chain',
      category: 'approved_already'
    },
    {
      patterns: [
        'invalid deposit denom',
        'invalid deposit',
        'spendable balance.*erc20.*insufficient funds',
        'spendable balance.*smaller than.*erc20'
      ],
      userMessage: 'This listing uses USDC.e on Nibiru. You need USDC.e (canonical USDC), not USDC from Noble. Use the Nibiru or Sai app to deposit USDC from Ethereum, Arbitrum, or Base—enter your Nibiru address as recipient.',
      technicalMessage: 'Invalid deposit denom or insufficient USDC.e balance',
      category: 'invalid_deposit_denom'
    },
    {
      patterns: ['unavailable period', 'unavailableperiod'],
      userMessage: 'Someone has already reserved this property for the selected dates. Please choose different dates.',
      technicalMessage: 'Requested period unavailable',
      category: 'reservation_conflict'
    },
    {
      patterns: ['rental is still active', 'rental exists', 'active rental'],
      userMessage: 'This property has an active rental and cannot be modified at this time.',
      technicalMessage: 'Property has active rental period',
      category: 'rental_conflict'
    },
    {
      patterns: [
        'reservation period overlaps',
        'period already reserved',
        'dates not available',
        'overlapping period',
        'period conflict',
        'set_reservation_for_short_term',
        'reservation_for_short_term'
      ],
      userMessage: 'Someone has already reserved this property for the selected dates. Please choose different dates.',
      technicalMessage: 'Reservation period conflict detected',
      category: 'reservation_conflict'
    },
    {
      patterns: [
        'gas.*set_reservation',
        'gas.*reservation.*failed',
        'out of gas.*reservation',
        'gas limit.*reservation'
      ],
      userMessage: 'Someone has already reserved this property for the selected dates. Please choose different dates.',
      technicalMessage: 'Reservation failed - likely due to date conflict',
      category: 'reservation_conflict'
    },
    {
      patterns: ['insufficient funds', 'not enough', 'balance too low'],
      userMessage: 'Insufficient balance to complete this transaction. Please add funds to your wallet.',
      technicalMessage: 'Insufficient account balance',
      category: 'insufficient_funds'
    },
    {
      patterns: ['insufficient deposit amount', 'deposit amount', 'insufficient.*deposit'],
      userMessage: 'The payment amount is too small or incorrectly calculated. Please refresh the page and try again.',
      technicalMessage: 'Insufficient deposit amount for contract execution',
      category: 'insufficient_deposit'
    },
    {
      patterns: [
        'gas(?!.*reservation)',
        'out of gas(?!.*reservation)', 
        'gas limit(?!.*reservation)'
      ],
      userMessage: 'Transaction failed due to gas limit. Please try again with higher gas settings.',
      technicalMessage: 'Gas limit exceeded',
      category: 'gas_error'
    },
    {
      patterns: ['sequence mismatch', 'account sequence'],
      userMessage: 'Transaction sequence error. Please wait a moment and try again.',
      technicalMessage: 'Account sequence mismatch',
      category: 'sequence_error'
    },
    {
      patterns: ['rejected', 'declined', 'cancelled', 'user rejected'],
      userMessage: 'Transaction was cancelled. Please try again if you want to proceed.',
      technicalMessage: 'User rejected transaction',
      category: 'user_rejected'
    },
    {
      patterns: ['timeout', 'timed out', 'request timeout'],
      userMessage: 'Transaction timed out. The network might be busy. Please try again.',
      technicalMessage: 'Transaction timeout',
      category: 'timeout'
    },
    {
      patterns: ['network', 'connection', 'chain id', 'wrong network'],
      userMessage: 'Network connection issue. Please check your wallet is connected to the correct network.',
      technicalMessage: 'Network connection error',
      category: 'network_error'
    },
    {
      patterns: ['not connected', 'no wallet', 'wallet not found'],
      userMessage: 'Wallet connection lost. Please reconnect your wallet and try again.',
      technicalMessage: 'Wallet not connected',
      category: 'wallet_disconnected'
    },
    {
      patterns: ['does not exist on chain', 'nibi'],
      userMessage: 'Not enough NIBI tokens in your wallet to pay for transaction fees.',
      technicalMessage: 'Insufficient NIBI for gas fees',
      category: 'gas_token_insufficient'
    },
    {
      patterns: ['is too short', 'minimum stay'],
      userMessage: 'Your requested stay is shorter than the host\'s minimum stay requirement.',
      technicalMessage: 'Stay duration below minimum',
      category: 'minimum_stay'
    },
    {
      patterns: ['503', 'server error', 'service unavailable'],
      userMessage: 'Server is temporarily unavailable. Please try again in a few minutes.',
      technicalMessage: 'Server error 503',
      category: 'server_error'
    },
    {
      patterns: ['slippage', 'price changed'],
      userMessage: 'Price changed during transaction. Please try again.',
      technicalMessage: 'Price slippage exceeded',
      category: 'slippage'
    },
    {
      patterns: ['limit exceeded', 'max limit', 'exceeds limit'],
      userMessage: 'This transaction exceeds platform limits. Please try a smaller amount.',
      technicalMessage: 'Transaction limit exceeded',
      category: 'limit_exceeded'
    },
    {
      patterns: ['contract error', 'wasm contract failed', 'execute wasm contract failed'],
      userMessage: 'Smart contract execution failed. This might be due to invalid parameters or contract state.',
      technicalMessage: 'Smart contract execution error',
      category: 'contract_error'
    },
    {
      patterns: ['query failed', 'rpc error'],
      userMessage: 'Network query failed. Please check your connection and try again.',
      technicalMessage: 'RPC query error',
      category: 'query_error'
    }
  ];

  // Find matching error pattern
  for (const mapping of errorMappings) {
    for (const pattern of mapping.patterns) {
      // Use regex for more flexible pattern matching
      const regex = new RegExp(pattern, 'i');
      if (regex.test(error)) {
        return {
          userMessage: mapping.userMessage,
          technicalMessage: mapping.technicalMessage,
          category: mapping.category,
          originalError: errorMessage
        };
      }
    }
  }

  // Default fallback for unknown errors
  return {
    userMessage: 'Transaction failed due to an unexpected error. Please try again or contact support if the issue persists.',
    technicalMessage: 'Unknown error occurred',
    category: 'unknown_error',
    originalError: errorMessage
  };
};

/**
 * Formats error for display in UI
 * @param {string} errorMessage - Raw error message
 * @param {boolean} showTechnical - Whether to show technical details
 * @returns {Object} - Formatted error object
 */
export const formatErrorForDisplay = (errorMessage, showTechnical = false) => {
  const parsed = parseErrorMessage(errorMessage);
  
  return {
    title: 'Transaction Failed',
    message: parsed.userMessage,
    technicalDetails: showTechnical ? {
      category: parsed.category,
      technicalMessage: parsed.technicalMessage,
      originalError: parsed.originalError
    } : null,
    category: parsed.category,
    timestamp: new Date().toISOString()
  };
};

/**
 * Gets error category icon
 * @param {string} category - Error category
 * @returns {string} - Icon name or emoji
 */
export const getErrorIcon = (category) => {
  const iconMap = {
    approved_already: '✅',
    invalid_deposit_denom: '💵',
    rental_conflict: '🏠',
    reservation_conflict: '📅',
    insufficient_funds: '💰',
    insufficient_deposit: '💵',
    gas_error: '⛽',
    sequence_error: '🔄',
    user_rejected: '❌',
    timeout: '⏰',
    network_error: '🌐',
    wallet_disconnected: '🔌',
    gas_token_insufficient: '🪙',
    minimum_stay: '📅',
    server_error: '🖥️',
    slippage: '📈',
    limit_exceeded: '📊',
    contract_error: '📋',
    query_error: '🔍',
    unknown_error: '❓'
  };
  
  return iconMap[category] || '❓';
};

/**
 * Gets error category color for UI
 * @param {string} category - Error category
 * @returns {string} - CSS color class or hex value
 */
export const getErrorColor = (category) => {
  const colorMap = {
    approved_already: '#10b981', // emerald
    invalid_deposit_denom: '#f59e0b', // amber
    rental_conflict: '#f59e0b', // amber
    reservation_conflict: '#f59e0b', // amber
    insufficient_funds: '#ef4444', // red
    insufficient_deposit: '#f97316', // orange
    gas_error: '#f97316', // orange
    sequence_error: '#3b82f6', // blue
    user_rejected: '#6b7280', // gray
    timeout: '#8b5cf6', // violet
    network_error: '#06b6d4', // cyan
    wallet_disconnected: '#ef4444', // red
    gas_token_insufficient: '#f59e0b', // amber
    minimum_stay: '#10b981', // emerald
    server_error: '#ef4444', // red
    slippage: '#f59e0b', // amber
    limit_exceeded: '#8b5cf6', // violet
    contract_error: '#ef4444', // red
    query_error: '#3b82f6', // blue
    unknown_error: '#6b7280' // gray
  };
  
  return colorMap[category] || '#6b7280';
};

/**
 * Determines if error is retryable
 * @param {string} category - Error category
 * @returns {boolean} - Whether the error is retryable
 */
export const isRetryableError = (category) => {
  const retryableCategories = [
    'sequence_error',
    'timeout',
    'network_error',
    'server_error',
    'query_error',
    'gas_error'
  ];
  
  return retryableCategories.includes(category);
};

/**
 * Gets suggested action for error
 * @param {string} category - Error category
 * @returns {string} - Suggested action text
 */
export const getSuggestedAction = (category) => {
  const actionMap = {
    approved_already: 'Refresh the page to see the updated reservation status.',
    invalid_deposit_denom: 'Use Nibiru or Sai app to deposit USDC from Ethereum/Arbitrum/Base with your Nibiru address as recipient.',
    rental_conflict: 'Wait for the current rental to end before making changes.',
    reservation_conflict: 'Please select different dates that are available for booking.',
    insufficient_funds: 'Add more funds to your wallet and try again.',
    insufficient_deposit: 'Refresh the page and verify the payment amount before trying again.',
    gas_error: 'Try again with higher gas settings.',
    sequence_error: 'Wait a moment and try again.',
    user_rejected: 'Review the transaction details and try again.',
    timeout: 'Check your internet connection and try again.',
    network_error: 'Switch to the correct network in your wallet.',
    wallet_disconnected: 'Reconnect your wallet and try again.',
    gas_token_insufficient: 'Add NIBI tokens to your wallet for transaction fees.',
    minimum_stay: 'Adjust your stay duration to meet the minimum requirement.',
    server_error: 'Wait a few minutes and try again.',
    slippage: 'Try again when the price is more stable.',
    limit_exceeded: 'Reduce the transaction amount and try again.',
    contract_error: 'Check if the property is available and try again.',
    query_error: 'Check your internet connection and try again.',
    unknown_error: 'Contact support if this error persists.'
  };
  
  return actionMap[category] || 'Please try again or contact support.';
};